import NotificationService from "../external/ensolvers-core-frontend-mui/submodules/fox-typescript/services/NotificationService";
import Timeoff from "../models/Timeoff";
import WorkLogService from "../services/WorkLogService";
import { TimeOffStatus } from "./enums/TimeOffStatus";
import OpsDateUtils from "./OpsDateUtils";
import OpsStringUtils from "./OpsStringUtils";
import RoleUtils from "./RoleUtils";

export default class TimeOffUtils {
  public static userCanChangeStatus = (timeOff: Timeoff, userExternalId: string | undefined): boolean => {
    return (
      (RoleUtils.isAdmin() && timeOff.requesterExternalId !== userExternalId) ||
      (RoleUtils.isManager() && timeOff.requesterExternalId !== userExternalId)
    );
  };

  public static userCanOpenOrCancel = (timeOff: Timeoff, userExternalId: string | undefined): boolean => {
    return timeOff.requesterExternalId === userExternalId;
  };

  public static timeOffCanBeCancelled = (timeOff: Timeoff): boolean => {
    return (
      timeOff.status !== TimeOffStatus.REJECTED &&
      timeOff.status !== TimeOffStatus.CANCELLED &&
      timeOff.status.replace("_", " ") !== TimeOffStatus.PENDING_CANCELLATION
    );
  };

  public static timeOffCanBeEdited = (timeOff: Timeoff): boolean => {
    return (
      timeOff.status.replace("_", " ") === TimeOffStatus.PENDING || timeOff.status.replace("_", " ") === TimeOffStatus.ADD_DOCUMENTATION
    );
  };

  public static getUserTimeoffs = (paramAssignationExternalId: string, selectedDate: Date): Promise<Timeoff[]> => {
    return new Promise((resolve) => {
      WorkLogService.findTimeOffsByRequesterAndDate(paramAssignationExternalId, selectedDate.getFullYear(), selectedDate.getMonth() + 1)
        .then((response) => {
          const content = response.getContent();
          resolve(content);
        })
        .catch(() => {
          NotificationService.error("Error getting TimeOffs");
        });
    });
  };

  public static createTimeOffWorklogs = (data: Timeoff) => {
    let localDate = new Date();
    const internalDescription = `${data.type}, approved on ${
      data.approvedDate !== undefined ? data.approvedDate : OpsDateUtils.formatInUTCAsDDMMMYYY(localDate)
    }, by `;
    const timeOffWorklog = {
      hours: 0,
      internalHours: 0,
      adjustmentHours: 0,
      description: "Time off",
      internalDescription: internalDescription,
      bonusHours: 0,
      shortDay: data.shortStartDate,
      externalId: undefined,
      assignationExternalId: "",
      assignationName: "",
      details: {
        tasksInProgress: [],
        resolvedIssues: [],
        blockers: [],
        tasksDone: [],
        prsPending: [],
        reportedIssues: [],
      },
    };

    WorkLogService.createTimeOffWorklogs(timeOffWorklog, data.shortStartDate, data.shortEndDate, data.halfDay, data.requesterExternalId)
      .then((response) => {
        response.getContent().length === 0
          ? NotificationService.info("Status changed successfully. Worklogs created")
          : NotificationService.warn("Operation completed. Some worklogs could not be created, check assignation and position start dates");
      })
      .catch(() => {
        NotificationService.warn("Operation completed. Some worklogs could not be created, check assignation and position start dates");
      });
  };

  public static deleteTimeOffWorklogs = (data: Timeoff) => {
    WorkLogService.deleteTimeOffWorklogs(data.shortStartDate, data.shortEndDate, data.requesterExternalId)
      .then((response) => {
        response.getContent().length === 0
          ? NotificationService.info("Status changed successfully. Worklogs deleted")
          : NotificationService.info("Status changed successfully");
      })
      .catch(() => {
        NotificationService.warn("Operation completed. Some worklogs could not be eliminated");
      });
  };

  public static timeoffStatusMessage = (timeoff: Timeoff): string => {
    return `${timeoff.requester.firstName} ${timeoff.requester.lastName} requested some time off from ${OpsDateUtils.formatInUTCAsDDDMMMYYY(
      timeoff.shortStartDate
    )} to ${OpsDateUtils.formatInUTCAsDDDMMMYYY(timeoff.shortEndDate)}.`;
  };

  public static getTimeoffDetails = (instance: Timeoff) => {
    const halfDay = instance.halfDay ? " - half day " : "";
    return OpsStringUtils.capitalize(instance.type).concat(
      ": ",
      OpsDateUtils.formatInUTCAsDDDMMMYYY(instance.shortStartDate),
      " - ",
      OpsDateUtils.formatInUTCAsDDDMMMYYY(instance.shortEndDate),
      halfDay
    );
  };

  public static getStatusColor = (instance: Timeoff) => {
    const status = (instance.status = instance.status.replace("_", " "));

    switch (status) {
      case TimeOffStatus.APPROVED:
        return "green";
      case TimeOffStatus.PENDING:
      case TimeOffStatus.ADD_DOCUMENTATION:
      case TimeOffStatus.PENDING_CANCELLATION:
        return "orange";
      case TimeOffStatus.REJECTED:
        return "red";
      case TimeOffStatus.CANCELLED:
        return "red";
      default:
        return "GREY";
    }
  };
}
